import * as React from 'react';
import styled from 'styled-components';
import {TscStyles, TscThemeName, TscThemeNames, Spinner} from '@techsmith/tsc-cloud-style-guide';
import {StyledAnchor} from '../util/StyledElements';
import {useTscTheme} from '../../context/themeProvider';

export const Table = styled.ul`
   list-style-type: none;
   min-height: 5rem;
   display: grid;
   grid-template-columns: repeat( auto-fit, minmax(14rem, 1fr) );
   grid-gap: 1rem;
   padding: 0;
`;

const TableWrapper = styled.div`
   margin-top: 1rem;
`;

const TableItem = styled.li<{theme: TscThemeNames}>`
   padding: .5rem 0;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.mediumDark};
   background: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.lightMedium};
   border: 1px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.dark : TscStyles.color.ui.dawn.medium};
   padding: .5rem 1rem;
   border-radius: ${TscStyles.border.radius.md};
   margin-right: 0;
   align-items: center;
`;

const TableHeaderItem = styled.div<{theme: TscThemeNames}>`
   padding: .5rem 0;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.mediumDark};
   background: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.lightMedium};
   border: 1px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.dark : TscStyles.color.ui.dawn.medium};
   padding: .5rem 1rem;
   border-radius: ${TscStyles.border.radius.sm};
   margin-right: 0;
   align-items: center;
   display: none;
   font-weight: 600;
   text-transform: uppercase;
   font-size: .75rem;
`;

const ItemCell = styled.div<{theme: TscThemeNames}>`
   display: grid;
   grid-template-columns: 116px 2fr;
   grid-gap: 12px;
   align-items: center;
   font-size: 0.875rem;
   margin-bottom: 0.5rem;

   a {
      color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.mediumDark};
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
   }

   span {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
   }

   &:before {
     margin-right: 1rem;
     display: block;
     font-weight: 600;
     text-transform: uppercase;
     font-size: .75rem;
   }
`;

const SmallViewLabel = styled.div`
   display: block;
   font-weight: 600;
   text-transform: uppercase;
   font-size: .75rem;
`;

export const EmptyData = styled(TableItem)`
   grid-template-columns: none !important;
   grid-template: none !important;
   text-align: center;
   font-style: italic;
   padding-top: 1.75rem;
`;

export const DataTableBase: React.FC<IDataTableProps> = ({columnLabels, tableRows, emptyStateLabel, isLoading = false}) => {
   const theme = useTscTheme();
   const createCell = (rowItem: string | React.JSX.Element | number, colIndex: number, link?: string) => (

      <ItemCell
         // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
         key={`${rowItem}-${colIndex}`}
         theme={theme}
      >
         <SmallViewLabel>
            {columnLabels[colIndex]}
         </SmallViewLabel>
         {
            colIndex === 0 && link ? (
               <StyledAnchor href={link}>
                  {rowItem}
               </StyledAnchor>
            ) :
               typeof rowItem === 'number' || typeof rowItem === 'string' ? (
                  <span>
                     {rowItem}
                  </span>
               ) : rowItem
         }
      </ItemCell>
   );

   const createRow = (row: ITableRowContent, rowIndex: number) => (
      <TableItem
         key={rowIndex}
         theme={theme}
         className="data-table-row"
      >
         {row.tableRow.map((rowItem: string | React.JSX.Element | number, index: number) => createCell(rowItem, index, row.link))}
         {row.button}
      </TableItem>
   );

   const LoadingState =
      (
         <TableWrapper
            data-test-id="loading-table-wrapper"
         >
            <Table>
               <EmptyData theme={theme}>
                  <Spinner
                     testId="loading-state-spinner"
                  />
               </EmptyData>
            </Table>
         </TableWrapper>
      );

   const emptyState =
      (
         <TableWrapper
            data-test-id="empty-table-wrapper"
         >
            <Table>
               <EmptyData theme={theme}>
                  {emptyStateLabel}
               </EmptyData>
            </Table>
         </TableWrapper>
      );

   const nonEmptyState =
      (
         <TableWrapper
            data-test-id="non-empty-table-wrapper"
         >
            <TableHeaderItem
               theme={theme}
               className="data-table-header"
            >
               {columnLabels.map((label: string, index: number) => (
                  <div key={`${label}-${index}`}>
                     {label}
                  </div>
               ))}
            </TableHeaderItem>
            <Table>
               {tableRows.map((row: ITableRowContent, index: number) => createRow(row, index))}
            </Table>
         </TableWrapper>
      );

   return isLoading ? LoadingState : !tableRows.length ? emptyState : nonEmptyState;
};

export interface IDataTableProps {
   columnLabels: string[];
   emptyStateLabel: string;
   tableRows: ITableRowContent[];
   isLoading?: boolean;
}

export interface ITableRowContent {
   tableRow: (string | React.JSX.Element | number)[];
   link?: string;
   button?: React.ReactElement;
}

export default React.memo(DataTableBase);
